import React from "react";
import {
	FormControl,
	Select,
	MenuItem,
	SelectChangeEvent,
	Avatar,
	Box,
	Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { UserLanguage } from "@/enum/user.enum";

interface Language {
	code: string;
	name: string;
	image: string;
}

interface LanguageSelectProps {
	languages: Language[];
	currentLanguage: UserLanguage;
	handleLanguageChange: (langCode: UserLanguage) => void;
}

const LanguageSelect: React.FC<LanguageSelectProps> = ({
	currentLanguage,
	languages,
	handleLanguageChange,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`courses.language.${key}`);

	const onChange = (event: SelectChangeEvent<string>) => {
		handleLanguageChange(event.target.value as UserLanguage);
	};

	return (
		<FormControl
			fullWidth
			size="small"
			sx={{
				minWidth: 120,
			}}
			variant="outlined"
		>
			<Typography
				sx={{
					color: "text.primary",
					fontWeight: 500,
					mb: 0.5,
				}}
				variant="body2"
			>
				{ts("language")}
			</Typography>

			<Select
				displayEmpty
				renderValue={(selected) => {
					const lang = languages.find((l) => l.code === selected);

					return lang ? (
						<Box alignItems="center" display="flex" gap={1}>
							<Avatar
								alt={`Flag of ${lang.name}`}
								src={lang.image}
								sx={{ width: 20, height: 20 }}
							/>
							{ts(`${lang.name.toLowerCase()}`)}
						</Box>
					) : (
						<span> {ts("select")}</span>
					);
				}}
				sx={{
					mt: 0.5,
					backgroundColor: "#fff",
				}}
				value={currentLanguage}
				variant="outlined"
				onChange={onChange}
			>
				{languages.map((lang) => (
					<MenuItem key={lang.code} value={lang.code}>
						<Box alignItems="center" display="flex" gap={1}>
							<Avatar
								alt={`Flag of ${lang.code}`}
								src={lang.image}
								sx={{ width: 20, height: 20 }}
							/>
							{ts(`${lang.name.toLowerCase()}`)}
						</Box>
					</MenuItem>
				))}
			</Select>
		</FormControl>
	);
};

export default LanguageSelect;
